import React, { useEffect, useState } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';

// Detect iOS Safari (no install prompt — must use "Add to Home Screen" manually)
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
  return isIos && isSafari;
}

// Already installed as a PWA?
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as any).standalone === true)
  );
}

const DISMISSED_KEY = 'pwa-install-dismissed';

const InstallPrompt: React.FC = () => {
  // Android / Chrome: deferred install prompt
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);

  // iOS: manual instructions dialog
  const [showIosDialog, setShowIosDialog] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Already installed
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    // iOS: show the instructions banner once per session
    if (isIosSafari()) {
      setShowIosDialog(true);
      return;
    }

    // Android / Chrome: capture the beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroidBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowAndroidBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setShowAndroidBanner(false);
    setShowIosDialog(false);
  };

  return (
    <>
      {/* Android / Chrome install banner */}
      <Snackbar
        open={showAndroidBanner}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 0, sm: 2 } }}
      >
        <Alert
          severity="info"
          icon={<GetAppIcon />}
          onClose={handleDismiss}
          action={
            <Button color="inherit" size="small" onClick={handleAndroidInstall}>
              Install
            </Button>
          }
          sx={{ width: '100%', alignItems: 'center' }}
        >
          Add Squadhatch to your home screen for the best experience.
        </Alert>
      </Snackbar>

      {/* iOS instructions dialog */}
      <Dialog
        open={showIosDialog}
        onClose={handleDismiss}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IosShareIcon color="primary" />
          Add to Home Screen
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Install Squadhatch for quick access, offline support, and push notifications.
            </Typography>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Tap the <strong>Share</strong> button{' '}
                  <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />{' '}
                  at the bottom of your browser.
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Scroll down and tap <strong>"Add to Home Screen"</strong>.
                </Typography>
              </Box>
              <Box component="li">
                <Typography variant="body2">
                  Tap <strong>Add</strong> — done!
                </Typography>
              </Box>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismiss} color="primary" variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallPrompt;
